import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import logo from "../../assets/logo/giraffe-logo.svg";
import { isApiProblem, problemMessage } from "../../shared/api/problem";
import type { FieldError, Locale } from "../../shared/api/types";
import { authText, isPasswordErrorCode, passwordErrorText, type AuthTextKey } from "../../shared/i18n/authText";
import { meQueryKey } from "../../shared/session/queries";
import { ErrorSummary, FieldMessage } from "../../shared/forms/ErrorSummary";
import type { FieldIssue } from "../../shared/forms/formErrors";
import { useFormErrors } from "../../shared/forms/useFormErrors";
import { changePassword, logout } from "./api";

type FieldName = "currentPassword" | "newPassword" | "confirmPassword";
type Values = Record<FieldName, string>;
const MISSING: Record<FieldName, AuthTextKey> = { currentPassword: "currentMissing", newPassword: "newMissing", confirmPassword: "confirmMissing" };

const FIELDS: { name: FieldName; label: AuthTextKey; autoComplete: string; hint?: AuthTextKey }[] = [
  { name: "currentPassword", label: "currentPassword", autoComplete: "current-password" },
  // The policy hint (UX-01 §4.15) is linked to the field; it names no symbol rule, which the current policy does not have.
  { name: "newPassword", label: "newPassword", autoComplete: "new-password", hint: "newPasswordHint" },
  { name: "confirmPassword", label: "confirmPassword", autoComplete: "new-password" },
];

/**
 * Auth ruling (16 Sep): when `Me.mustChangePassword` is true, this screen replaces every route until the password is
 * changed. POST /auth/change-password → 204 → refetch Me, which returns the user to the page they asked for.
 * 422 validation-failed shows the server's field errors next to their fields.
 */
/** Which input a 422 error belongs to: its `field`, else by Identity code (a mismatch is about the current password). */
function fieldOf(error: FieldError): "currentPassword" | "newPassword" | undefined {
  const field = (error.field ?? "").replace(/^\//, "");
  if (field === "currentPassword" || field === "newPassword") return field;
  if (error.code === "PasswordMismatch") return "currentPassword";
  return isPasswordErrorCode(error.code) ? "newPassword" : undefined;
}

export function ChangePasswordPage({ locale, email, onChanged }: { locale: Locale; email: string; onChanged?: () => void }) {
  const id = useId();
  const t = (key: AuthTextKey) => authText(key, locale);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Uncontrolled password inputs (password managers fill them); typing is mirrored here so errors follow the values.
  const [values, setValues] = useState<Values>({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const form = useFormErrors({
    values,
    validate: (current): FieldIssue[] => {
      const missing = FIELDS.filter((field) => !current[field.name]).map((field) => ({ field: field.name, message: t(MISSING[field.name]) }));
      if (missing.length > 0) return missing;
      return current.newPassword !== current.confirmPassword ? [{ field: "confirmPassword", message: t("confirmMismatch") }] : [];
    },
    order: FIELDS.map((field) => field.name),
    idOf: (field) => `${id}-${field}`,
  });

  useEffect(() => {
    document.title = `${authText("changeTitle", locale)} · Giraffe`;
  }, [locale]);

  const mutation = useMutation({
    mutationFn: changePassword,
    meta: { authFlow: true },
    onSuccess: async () => {
      form.clearServer();
      onChanged?.();
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: (error) => {
      if (isApiProblem(error) && error.status === 422) {
        const found: Partial<Record<FieldName, string>> = {};
        // UX-01 §4.15: errors[].code (ASP.NET Identity) maps to EN/TH text; several rules on one field read together.
        for (const fe of error.fieldErrors) {
          const field = fieldOf(fe);
          if (!field) continue;
          const text = passwordErrorText(fe, locale);
          found[field] = found[field] ? `${found[field]} ${text}` : text;
        }
        const fieldIssues = Object.entries(found).map(([field, message]) => ({ field, message }));
        form.setServer({ fieldIssues, general: [] }, fieldIssues.length > 0 ? null : problemMessage(error));
        return;
      }
      form.setServer({ fieldIssues: [], general: [] }, problemMessage(error));
    },
  });

  function readValues(element: HTMLFormElement): Values {
    const data = new FormData(element);
    const text = (name: FieldName) => {
      const value = data.get(name);
      return typeof value === "string" ? value : "";
    };
    return { currentPassword: text("currentPassword"), newPassword: text("newPassword"), confirmPassword: text("confirmPassword") };
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const current = readValues(event.currentTarget);
    setValues(current);
    if (!form.attempt(current)) return;
    mutation.mutate({ currentPassword: current.currentPassword, newPassword: current.newPassword });
  }

  async function onSignOut() {
    try {
      await logout();
    } finally {
      queryClient.clear();
      await navigate("/login", { replace: true });
    }
  }

  return (
    <main className="gf-login" id="main">
      <div className="gf-login-card stack">
        <img src={logo} alt="Giraffe" width={140} height={36} />
        <h1>{t("changeTitle")}</h1>
        <p className="sub">{t("changeIntro")}</p>
        {email && <p className="help">{email}</p>}
        <ErrorSummary {...form.summary} title={t("changeErrors")} />
        <form className="gf-form stack" onSubmit={onSubmit} onChange={(event) => setValues(readValues(event.currentTarget))} noValidate>
          {FIELDS.map((field) => {
            const error = form.errorOf(field.name);
            const inputId = `${id}-${field.name}`;
            return (
              <div className="gf-field" key={field.name}>
                <label htmlFor={inputId}>{t(field.label)}</label>
                <input
                  id={inputId}
                  name={field.name}
                  type="password"
                  autoComplete={field.autoComplete}
                  className="in"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={[field.hint ? `${inputId}-hint` : "", error ? `${inputId}-error` : ""].filter(Boolean).join(" ") || undefined}
                />
                {field.hint && (
                  <span className="help" id={`${inputId}-hint`}>
                    {t(field.hint)}
                  </span>
                )}
                <FieldMessage id={inputId} message={error} />
              </div>
            );
          })}
          <button className="btn" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t("changeSaving") : t("changeSubmit")}
          </button>
        </form>
        <button type="button" className="linkish" onClick={() => void onSignOut()}>
          {t("signOut")}
        </button>
      </div>
    </main>
  );
}
